import * as React from 'react';
import { RouteComponentProps, useLocation } from 'react-router';
import styled from 'styled-components';
import { animated } from 'react-spring';
import { useMutation } from '@apollo/client';

import { SiteContext } from '../../../../utils/context/SiteContext';

import { Container } from '../../../layout/Container';

import { Heading } from '../../../ui/text/Heading';
import { Paragraph } from '../../../ui/text/Paragraph';

import { useFadeTransition } from '../../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../../utils/seo/Index';

import { BaseHeroContent, Hero } from '../../../ui/hero/Index';

import { useForm } from '../../../../utils/hooks/useForm';
import { useFormTransition } from '../../../../utils/hooks/useFormTransition';

import { Section } from '../../../layout/Section';
import { Thanks } from '../../../layout/Thanks';

import { Input } from '../../../ui/form/Input';
import { FormError } from '../../../ui/form/FormError';
import { Form, FormRow } from '../../../ui/form/Form';
import { ButtonGroup } from '../../../ui/button/ButtonGroup';
import { Button } from '../../../ui/button/Button';
import { ButtonLink } from '../../../ui/link/ButtonLink';
import { createGTMEvent } from '../../../../utils/tracking/gtm';
import {
  GeneralValuation,
  GeneralValuationVariables
} from '../../../../__types__/generated/GeneralValuation';
import { GENERAL_VALUATION_MUTATION } from '../../../../../client/__graphql__/mutations/valuation';
import {
  LeadType,
  PageType
} from '../../../../__types__/generated/globalTypes';
import { useDigtectiveContext } from '../../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const EtakstNokkelenBoligsalg: React.FC = () => {
  const location = useLocation();
  const { routeConfig } = React.useContext(SiteContext);

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });

  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [lead, { error }] = useMutation<
    GeneralValuation,
    GeneralValuationVariables
  >(GENERAL_VALUATION_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead.valuation?.success) {
        window.scroll(0, 0);
        setPosted(true);

        // GTM tracking

        createGTMEvent({
          event: 'pmFormSubmission',
          gaEvent: 'RT_VALUATION',
          gaCategory: 'RT_VALUATION',
          gaAction: 'RT_SEND_VALUATION'
        });
      }
    }
  });

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: LeadType.VERDIVURDERING,
              pageType: PageType.GENERELL
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="E-takst. Vet du hva det er? - PrivatMegleren"
        description="Skal du selge eiendom, er E-takst nøkkelen til et godt eiendomssalg."
        url={location.pathname}
      />
      <Hero
        imageUrl="https://cdn.reeltime.no/pm_assets/content/etakst.jpeg"
        opacity={0.2}
        height="600px"
        className="top-header-margin"
        alt="bilde"
      >
        <HeroContent style={pageFadeIn}>
          <Heading tag="h1" center={true}>
            Skal du selge eiendom, er E-takst nøkkelen til et godt eiendomssalg.
            Vet du hva det er?
          </Heading>
        </HeroContent>
      </Hero>
      <Container
        className="padding-top"
        style={{ ...pageFadeIn, paddingTop: '80px', paddingBottom: '80px' }}
      >
        <Paragraph center>
          Det aller viktigste for deg som skal selge eiendom er antageligvis
          salgssummen. Men vel så viktig er e-taksten. Den gir deg nemlig en
          indikator på hvor mye du kan legge i et nytt eiendomskjøp, slik at du
          unngår overraskelser.
        </Paragraph>
        <Paragraph center>
          E-takst er en ny og velkommen standard som gir en god og dokumenterbar
          vurdering av eiendommens verdi. Før e-takst ble innført, gjorde megler
          en skjønnsmessig verdivurdering av eiendommer som skulle legges ut for
          salg. Prisvurdering av eiendom ble basert på udefinerte standarder, og
          meglerhusene opererte med ulike parametere. E-takst er et nyttig
          verktøy som stiller konkrete krav til dokumentasjon av meglers
          verdivurdering, slik at en prisvurdering alltid baseres på
          dokumenterbare analyser.Baseres på sammenligning med tilsvarende
          eiendom i samme område i nær fortid.
        </Paragraph>
        <Heading tag="h3" center>
          Dette påvirker verdi på eiendom
        </Heading>
        <Paragraph center>
          Temperaturen i eiendomsmarkedet er en av de faktorene som påvirker
          verdi på eiendommen din, og derfor nytter det ikke å sammenligne seg
          med naboen som solgte en tilsvarende eiendom for et år siden.
        </Paragraph>
        <Paragraph center>
          «Ferskvareprinsippet» er et viktig moment i alle eiendomsalg, og en
          eiendomsmegler vil alltid ta pulsen på dagens marked før e-takst
          settes. E-takst er derfor en komplett verdivurdering av eiendommen
          kombinert med eiendomsmarkedets puls på det tidspunktet du skal selge.
          Slik blir hele eiendomssalgsprosessen mer transparent, og du får en
          god pekepinn på hvilke verdier du sitter på før du beveger deg ut på
          eiendomsmarkedet.
        </Paragraph>
        <Heading tag="h3" center>
          Meglerens ekspertise gir en solid prisvurdering
        </Heading>
        <Paragraph center>
          Når megler skal beregne verdien av eiendommen din, er det tre
          hovedfaktorer som spiller inn. Areal, tilstanden på eiendommen samt
          beliggenhet. Innenfor disse tre punktene er det mange nyanser, men det
          er spesielt ett punkt du kan påvirke selv.
        </Paragraph>
        <Paragraph center>
          Tilstanden på eiendommen betyr alt fra eiendommens forfatning til
          materialvalg. Mange lurer for eksempel på om de bør pusse opp badet
          før de selger for å få en høyere e-takst. Rådet til alle som vil ha en
          god prisvurdering er å bruke eiendomsmegleren som en rådgiver fremfor
          å gamble i oppussingsprosjekter som kanskje ikke gir den gevinsten man
          håper.
        </Paragraph>
        <Paragraph center>
          Megleren er en eiendomsspesialist som har et trent blikk for hva det
          lønner seg å forbedre, og ikke minst, hva man kan la være å
          oppgradere. Men én ting er sikkert; mangler det lister, ligner
          inngangspartiet på en byggeplass eller har flisene falt ned i dusjen,
          så koster det ikke stort å få ting på stell. Helhetsinntrykket bør
          være ryddig når du skal selge eiendom.
        </Paragraph>
        <Image src="https://cdn.reeltime.no/pm_assets/content/etakst.jpeg" />
        <Heading tag="h3" center>
          E-takst er vinn-vinn
        </Heading>
        <Paragraph center>
          Når megler setter e-takst, kommuniserer denne med verktøyet
          Eiendomsverdi, som følger og registrerer alle aktiviteter i det norske
          eiendomsmarkedet til enhver tid. Når kjøperne kommer på visning, kan
          megleren bruke e-taksten som referansepunkt for å forklare hvordan
          verdien på eiendommen er evaluert. Dokumentasjonen gjør det enklere
          for potensielle kjøpere å danne seg et bilde av hva som gjør din
          eiendom spesielt attraktiv, samtidig som de kan være sikre på at
          verdien er satt ut i fra sammenlignbare forhold.
        </Paragraph>
        <FormWrapper style={fadeOut}>
          <Section className="form-section" style={{ paddingBottom: '10px' }}>
            <Heading tag="h2" center={true}>
              Kontakt
            </Heading>
          </Section>
          <Form onSubmit={handleSubmit} noValidate>
            {error &&
              error.graphQLErrors.map((error, index) => (
                <FormError key={`error_${index}`} message={error.message} />
              ))}
            {fields.map((item: any, index: number) => {
              return (
                <FormRow key={`field_${index}`}>
                  <Input
                    type={item.type}
                    name={item.name}
                    label={item.label}
                    labelAfter={item.labelAfter}
                    placeholder={item.placeholder}
                    value={item.value}
                    onChange={handleChange}
                    error={item.error}
                  />
                </FormRow>
              );
            })}
            <ButtonGroup>
              <Button
                type="primary"
                colorTheme="gold"
                disabled={loading}
                loading={loading}
              >
                Send
              </Button>
              <ButtonLink
                as="a"
                href={routeConfig.personvern.url}
                className="privacy-link-mobile"
              >
                Personvernpolicy
              </ButtonLink>
            </ButtonGroup>
          </Form>
        </FormWrapper>
        <Thanks style={fadeIn}>
          <Section className="form-section">
            <Heading tag="h1" center={true}>
              Tusen takk
            </Heading>
          </Section>
        </Thanks>
      </Container>
    </>
  );
};

export default EtakstNokkelenBoligsalg;

const FormWrapper = styled(animated.div)``;

const HeroContent = styled(animated.div)`
  ${BaseHeroContent}
  flex-flow: column;
  max-width: 960px;
`;

const Image: React.FC<{ src: string }> = ({ src }) => {
  return (
    <StyledImageWrapper>
      <img src={src} alt="bilde-inspirasjon" />
    </StyledImageWrapper>
  );
};

const StyledImageWrapper = styled.div`
  display: block;
  width: 100%;
  margin: 30px 0;

  img {
    width: 100%;
  }
`;
